import { Box, Button, IconButton, Link, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import SyncIcon from '@material-ui/icons/SyncAlt';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import SearchTextField from '../../components/SearchTextField';
import TableSkeleton from '../../components/TableSkeleton';
import { useTransferMutation, useTransfersWithSearchKey } from '../../hooks/queries/useTransfer';
import { useQueryStringState } from '../../hooks/useQueryStringState';
import useSnackbar from '../../hooks/useSnackbar';
import { downloadSeasonTransfers, TransferSearchType } from '../../services/api/Transfer';
import { ITransfer, IUser } from '../../services/types';
import { downloadCSVFromText } from '../../services/utils';
import useStyles from './TransferListStyles';
import TransferQuarterlyStories from './TransferQuarterlyStories';

interface IProps {
  quarterStateChanger: React.Dispatch<React.SetStateAction<number>>;
}

enum SortKeys {
  Writer = 'username',
  Coins = 'transferredCoins',
  Amount = 'transferredAmount',
}

enum Orders {
  Ascending = 1,
  Descending = -1,
}

const TransferQuarterlyList = (props: IProps) => {
  const classes = useStyles();
  const snackbar = useSnackbar();

  const [selectedQuarter, setSelectedQuarter] = useState<number>(0);
  const [unfoldedTransferIds, setUnfoldedTransferIds] = useState<number[]>([]);
  const [sort, setSort] = useState<SortKeys>(SortKeys.Writer);
  const [order, setOrder] = useState<Orders>(Orders.Ascending);

  const [searchType, setSearchType] = useQueryStringState<TransferSearchType>('quarterlySearchType', TransferSearchType.StoryId);
  const [searchKey, setSearchKey] = useQueryStringState<string>('quarterlySearchKey');

  const { data, isLoading } = useTransfersWithSearchKey(selectedQuarter, {
    searchType: searchKey ? searchType : undefined,
    searchKey: searchKey ? Number(searchKey) : undefined,
  });

  const { setTransferred } = useTransferMutation();

  const handleQuarterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedQuarter(Number(event.target.value));
    setSort(SortKeys.Writer);
    setOrder(Orders.Ascending);
  };

  const handleTransferClick = (transferId: number) => {
    setTransferred.mutate({
      transferId,
      quarter: selectedQuarter,
      searchKey: searchKey ? Number(searchKey) : undefined,
      searchType: searchKey ? searchType : undefined,
    });
  };

  const handleDownloadClick = async (writer: IUser, trackId: number) => {
    try {
      const { status, data } = await downloadSeasonTransfers(trackId);

      if (status === 200) {
        const fileName =
          selectedQuarter === 0
            ? moment().endOf('quarter').add(-1, 'quarter').format('YYYY_MMM')
            : moment(selectedQuarter, 'YYYYMM').format('YYYY_MMM');

        downloadCSVFromText(data, `${fileName}_Detail_${writer.username}_Report`);
      } else {
        throw new Error('Unable to download a CSV file.');
      }
    } catch (e) {
      snackbar.error(e);
    }
  };

  const handleToggleClick = (id: number) => {
    if (unfoldedTransferIds.indexOf(id) >= 0) {
      setUnfoldedTransferIds(unfoldedTransferIds.filter(item => item !== id));
    } else {
      setUnfoldedTransferIds(unfoldedTransferIds.concat(id));
    }
  };

  const handleSortChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSort(event.target.value as SortKeys);
  };

  const handleOrderChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setOrder(Number(event.target.value) as Orders);
  };

  const handleSearchTypeChange = (e: React.ChangeEvent<any>) => {
    setSearchType(e.target.value as TransferSearchType);
  };

  const handleSearchKeySubmit = (value: string) => {
    setSearchKey(value);
  };

  const handleSearchKeyCancel = () => {
    setSearchKey(undefined);
  };

  const quarterOptions = useMemo((): JSX.Element[] => {
    const startMoment = moment().year(2019).quarter(1).endOf('quarter');
    const nowMoment = moment().endOf('quarter').add(-1, 'quarter');
    const options = [
      <MenuItem key={`options-quarter-tobe`} value={0}>
        To Be Transferred: {nowMoment.format('YYYY Q')}Q
      </MenuItem>,
    ];

    while (nowMoment.isSameOrAfter(startMoment)) {
      const quarterMonth = nowMoment.format('YYYY0Q');
      options.push(
        <MenuItem key={`options-quarter-${quarterMonth}`} value={nowMoment.format('YYYYMM')}>
          Transferred: {nowMoment.format('YYYY Q')}Q
        </MenuItem>,
      );
      nowMoment.add(-1, 'quarter');
    }

    return options;
  }, []);

  const sortedData = useMemo((): ITransfer[] | undefined => {
    if (data) {
      return data.sort((a, b) => {
        if (sort === SortKeys.Writer) {
          return a.writer.username.toLowerCase() > b.writer.username.toLowerCase() ? order : -order;
        } else {
          return a[sort] > b[sort] ? order : -order;
        }
      });
    }
    return data;
  }, [data, sort, order]);

  const transferredCount = useMemo((): number => (data ? data.filter(item => item.transferredAt !== null).length : 0), [data]);

  useEffect(() => {
    props.quarterStateChanger(selectedQuarter);
  }, [props, selectedQuarter]);

  return (
    <>
      <Box marginBottom={'20px'} style={{ display: 'flex' }}>
        <Select value={selectedQuarter} onChange={handleQuarterChange}>
          {quarterOptions}
        </Select>
        <Box style={{ marginLeft: 'auto' }}>
          <Select value={searchType} onChange={handleSearchTypeChange}>
            <MenuItem value={TransferSearchType.StoryId}>Story Id</MenuItem>
            <MenuItem value={TransferSearchType.SeasonId}>Season Id</MenuItem>
          </Select>
          <SearchTextField
            type={'number'}
            defaultValue={searchKey}
            onValueSubmit={handleSearchKeySubmit}
            onCancel={handleSearchKeyCancel}
            isSearching={isLoading}
          />
        </Box>
      </Box>

      <Paper>
        <Box display="flex" alignItems="center" paddingTop={2} paddingLeft={2} paddingRight={2}>
          <Box flex="1">
            <span className={classes.sortLabel}>Transferred: </span>
            <span className={classes.countValue}>{transferredCount}</span>
            <span> / </span>
            <span className={classes.sortLabel}>Total: </span>
            <span className={classes.countValue}>{data ? data.length : 0}</span>
          </Box>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <span className={classes.sortIcon}>
              <SyncIcon style={{ fontSize: 16 }} />
            </span>
            <span className={classes.sortLabel}>Sort by:</span>
            <Select value={sort} onChange={handleSortChange} className={classes.sortSelect}>
              <MenuItem value={SortKeys.Writer}>Writer</MenuItem>
              <MenuItem value={SortKeys.Coins}>Coins</MenuItem>
              <MenuItem value={SortKeys.Amount}>Payable</MenuItem>
            </Select>
            <Select value={order} onChange={handleOrderChange} className={classes.sortSelect}>
              <MenuItem value={Orders.Ascending}>Ascending</MenuItem>
              <MenuItem value={Orders.Descending}>Descending</MenuItem>
            </Select>
          </Box>
        </Box>
        <Table component={'table'}>
          <TableHead component={'thead'} className={classes.headerRow}>
            <TableRow component={'tr'}>
              <TableCell component={'td'} align="center" style={{ width: 30 }}></TableCell>
              <TableCell component={'td'} align="left" style={{ width: 30 }}>
                No.
              </TableCell>
              <TableCell component={'td'} align="left">
                Writer (Payee)
              </TableCell>
              <TableCell component={'td'} align="center" style={{ width: 157 }}>
                Transfer Date
              </TableCell>
              <TableCell component={'td'} align="right">
                Coins Redeemed
              </TableCell>
              <TableCell component={'td'} align="right">
                Total Payable
              </TableCell>
              <TableCell component={'td'} align="center">
                Report
              </TableCell>
              <TableCell component={'td'}></TableCell>
              <TableCell component={'td'}></TableCell>
              <TableCell component={'td'}></TableCell>
              <TableCell component={'td'}></TableCell>
              <TableCell component={'td'}></TableCell>
              <TableCell component={'td'}></TableCell>
              <TableCell component={'td'}></TableCell>
              <TableCell component={'td'}></TableCell>
              <TableCell component={'td'}></TableCell>
            </TableRow>
          </TableHead>
          {isLoading && (
            <TableBody component={'tbody'}>
              <TableSkeleton rows={10} columns={16} isOriginalDefaultComponent={true} />
            </TableBody>
          )}
          {!isLoading &&
            sortedData &&
            sortedData.map((transfer: ITransfer, no: number) => {
              const { id, writer, transferredAt, transferredCoins, transferredAmount } = transfer;
              const isUnfolded = unfoldedTransferIds.indexOf(id) >= 0;

              return (
                <React.Fragment key={`transfer-quarterly-row-${id}`}>
                  <TableBody component={'tbody'} className={isUnfolded ? classes.selectedRow : ''}>
                    <TableRow component={'tr'} hover className={classes.tableRow}>
                      <TableCell component={'td'} align="center">
                        <IconButton size="small" onClick={() => handleToggleClick(id)}>
                          {isUnfolded && <KeyboardArrowUpIcon />}
                          {!isUnfolded && <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell component={'td'} align="left">
                        {sortedData.length - no}
                      </TableCell>
                      <TableCell component={'td'} align="left">
                        <Link href={`${process.env.CREW_DEFAULT_URI}/users/${writer.id}`} target="_blank">
                          {writer.username}
                        </Link>
                      </TableCell>
                      <TableCell component={'td'} align="center">
                        {transferredAt ? (
                          moment(transferredAt).format('YYYY-MM-DD HH:mm:ss')
                        ) : (
                          <Button variant="contained" color="primary" onClick={() => handleTransferClick(id)}>
                            TRANSFER
                          </Button>
                        )}
                      </TableCell>
                      <TableCell component={'td'} align="right">
                        {Number(transferredCoins).toLocaleString()}
                      </TableCell>
                      <TableCell component={'td'} align="right">
                        $ {Number(transferredAmount).toLocaleString('en-US', { maximumFractionDigits: 4 })}
                      </TableCell>
                      <TableCell component={'td'} align="left">
                        <Button variant="outlined" size="small" color="primary" onClick={() => handleDownloadClick(writer, id)}>
                          Download Report
                        </Button>
                      </TableCell>
                      <TableCell component={'td'}></TableCell>
                      <TableCell component={'td'}></TableCell>
                      <TableCell component={'td'}></TableCell>
                      <TableCell component={'td'}></TableCell>
                      <TableCell component={'td'}></TableCell>
                      <TableCell component={'td'}></TableCell>
                      <TableCell component={'td'}></TableCell>
                      <TableCell component={'td'}></TableCell>
                      <TableCell component={'td'}></TableCell>
                    </TableRow>
                  </TableBody>
                  {isUnfolded && <TransferQuarterlyStories transferId={id} />}
                </React.Fragment>
              );
            })}
          {!isLoading && (!sortedData || sortedData.length === 0) && (
            <TableBody component={'tbody'}>
              <TableRow component={'tr'}>
                <TableCell component={'td'} colSpan={15} align="center">
                  This Story(or Season) does not exist. Please Try Again.
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </Paper>
    </>
  );
};

export default TransferQuarterlyList;
